/**
 * Human-readable descriptions for validation checks (UI + PDF report).
 */

export const VALIDATION_CHECK_CATALOG = {
  'ai-abbrev-first-use': {
    howChecked:
      'Scans all protocol section text for abbreviation definitions (Full Term (ABBR)) and flags any bare abbreviation used before its first definition.',
    passCriteria: 'Every abbreviation appears as Full Term (ABBR) before standalone use.',
  },
  'ai-abbrev-heading-only': {
    howChecked: 'Parses HTML headings (h1–h6) and rejects headings that contain only uppercase abbreviations.',
    passCriteria: 'No heading consists solely of an abbreviation.',
  },
  'ai-abbrev-sentence-start': {
    howChecked:
      'Splits body text into sentences and flags those starting with abbreviations (except MedDRA, AIDS, UNICEF, etc.).',
    passCriteria: 'Sentences do not begin with unexpanded abbreviations.',
  },
  'ai-abbrev-plural': {
    howChecked: 'Regex scan for plural abbreviations written with apostrophe (e.g., CRF\'s).',
    passCriteria: 'Plural abbreviations use lowercase s without apostrophe (CRFs).',
  },
  'ai-abbrev-loa': {
    howChecked: 'Verifies a section title contains List of Abbreviations / Abbreviations.',
    passCriteria: 'List of Abbreviations section is present.',
  },
  'ai-style-hash-number': {
    howChecked: 'Scans text for # followed by digits used as "number".',
    passCriteria: 'Uses "No." instead of # for numbering.',
  },
  'ai-style-eg-ie': {
    howChecked: 'Detects mixing of dotted (e.g./i.e.) and comma (eg,/ie,) styles in the same document.',
    passCriteria: 'Single consistent e.g./i.e. style throughout.',
  },
  'ai-style-tnr-font': {
    howChecked:
      'Inspects inline font-family and font-size in section HTML against Draft 1.0 §4.1 allowed sizes (Times New Roman).',
    passCriteria: 'All inline fonts are Times New Roman with allowed point sizes.',
  },
  'ai-table-numbering': {
    howChecked: 'Extracts table captions/labels and verifies sequential Table 1, 2, 3… numbering.',
    passCriteria: 'Tables are numbered sequentially without gaps or duplicates.',
  },
  'ai-si-units': {
    howChecked: 'Compares unit symbols (kg, mg, mL, etc.) for consistency across text and tables.',
    passCriteria: 'SI units and symbols are used consistently.',
  },
  'ai-xref-informal': {
    howChecked: 'Flags informal phrases such as "see table below" or "figure above".',
    passCriteria: 'Cross-references use formal Table X / Figure X / Section X.X format.',
  },
  'ai-xref-targets': {
    howChecked: 'Matches in-text Table N / Figure N references to labels defined in the document.',
    passCriteria: 'Every cross-reference target exists in the document.',
  },
  'ai-gemini-oxford-comma': {
    howChecked: 'Gemini reviews genuine 3+-item series for a missing comma before the final "and".',
    passCriteria: 'Oxford comma used in applicable series.',
  },
  'ai-punct-quotes': {
    howChecked: 'Counts straight ASCII quotes/apostrophes vs typographic curly quotes.',
    passCriteria: 'Curly/smart quotes and apostrophes used in body text.',
  },
  'ai-punct-dates': {
    howChecked: 'Flags US-style dates (Month DD, YYYY) in running text.',
    passCriteria: 'European date format (DD Month YYYY) in running text.',
  },
  'ai-time-abbrev-body': {
    howChecked: 'Scans body text (excluding tables) for abbreviated time units (wk, mo, h, min).',
    passCriteria: 'Time units spelled out in running text; abbreviations only in tables/figures.',
  },
  'ai-numeric-sample-size': {
    howChecked: 'Extracts sample size mentions (N=, participants, subjects) across all sections.',
    passCriteria: 'No more than three distinct sample sizes without explanation.',
  },
  'ai-abbrev-articles': {
    howChecked: 'Checks a/an before uppercase abbreviations against phonetic first letter rules.',
    passCriteria: 'Article matches how the abbreviation is pronounced (an SAE, an NDA).',
  },
  'ai-tone-participant': {
    howChecked: 'Flags use of "subject(s)" in protocol body text.',
    passCriteria: 'Uses "participant(s)" as the default term.',
  },
  'ai-tone-person-first': {
    howChecked: 'Flags disease-first phrases such as "cancer patients".',
    passCriteria: 'Person-first language (patients with …).',
  },
  'ai-tone-gendered-pronouns': {
    howChecked: 'Flags he/she, him/her, his/her pronoun pairs.',
    passCriteria: 'No sex-specific pronoun pairs.',
  },
  'ai-tone-victim-blame': {
    howChecked: 'Flags phrases such as suffering from, afflicted with, failed treatment.',
    passCriteria: 'Neutral, non-blaming patient language.',
  },
  'ai-tone-socioeconomic': {
    howChecked: 'Flags noun phrases such as "the poor", "the unemployed".',
    passCriteria: 'Person-first socioeconomic wording.',
  },
  'ai-tone-speculative': {
    howChecked: 'Flags guarantee/promise/miracle/breakthrough/will ensure language.',
    passCriteria: 'No unintended promissory or speculative claims.',
  },
  'ai-doc-placeholders': {
    howChecked: 'Scans for [TBD], [INSERT], [TK], lorem ipsum and similar drafting markers.',
    passCriteria: 'No unresolved placeholder text.',
  },
  'ai-safety-latin-dosing': {
    howChecked: 'Flags bid/tid/qid/qd/prn in body text outside tables.',
    passCriteria: 'Latin dosing abbreviations spelled out in running text.',
  },
  'ai-safety-ismp-units': {
    howChecked: 'Flags numeric + U/IU dose patterns (ISMP error-prone abbreviations).',
    passCriteria: 'Units / international units spelled out.',
  },
  'ai-safety-dose-zeros': {
    howChecked: 'Flags trailing zeros (1.0 mg) and missing leading zeros (.5 mg).',
    passCriteria: 'Correct leading zeros; no trailing zeros in doses.',
  },
  'ai-units-ml-mcg': {
    howChecked: 'Flags cc for volume and µg/ug for micrograms in clinical dosing.',
    passCriteria: 'Uses mL and mcg as required.',
  },
  'ai-consistency-preferred-terms': {
    howChecked: 'Flags Phase III, prior to, utilize, package insert, protocol violations, redundancies, etc.',
    passCriteria: 'Preferred style-guide terms used consistently.',
  },
  'ai-symbols-comparison-words': {
    howChecked: 'Flags greater than / less than / at least wording where symbols are preferred.',
    passCriteria: 'Uses >, <, ≥, ≤ where appropriate.',
  },
  'ai-numbers-operator-spacing': {
    howChecked: 'Flags missing spaces around =, <, >, ≥, ≤, ± operators.',
    passCriteria: 'Spaces on both sides of mathematical operators.',
  },
  'ai-numbers-unit-spacing': {
    howChecked: 'Flags missing space before units and space before %.',
    passCriteria: 'Full space before units; no space before %.',
  },
  'ai-symbols-multiply': {
    howChecked: 'Flags letter x used as multiplication operator.',
    passCriteria: 'Uses × symbol for multiplication.',
  },
  'ai-punct-unit-period': {
    howChecked: 'Flags periods immediately after unit symbols (mg.).',
    passCriteria: 'No periods after unit symbols except sentence end.',
  },
  'ai-punct-double-space': {
    howChecked: 'Counts multiple spaces after . ! ? :',
    passCriteria: 'Exactly one space after sentence punctuation.',
  },
  'ai-xref-capitalize': {
    howChecked: 'Flags lowercase table/figure/section when citing a numbered item.',
    passCriteria: 'Capitalized Table/Figure/Section/Appendix/Listing designators.',
  },
  'ai-doc-ind-nct': {
    howChecked: 'Flags unresolved IND/NCT placeholders.',
    passCriteria: 'No unresolved regulatory identifier placeholders.',
  },
  'ai-doc-informed-consent': {
    howChecked: 'Searches section titles/text for informed consent / re-consent content.',
    passCriteria: 'Informed consent procedures are addressed.',
  },
  'ai-doc-incl-excl': {
    howChecked: 'Checks for inclusion and exclusion criteria headings/text.',
    passCriteria: 'Both inclusion and exclusion criteria are present.',
  },
  'ai-doc-analysis-populations': {
    howChecked: 'Looks for ITT/FAS/primary analysis population and safety population wording.',
    passCriteria: 'Primary analysis and safety populations are defined.',
  },
  'ai-punct-decade-apostrophe': {
    howChecked: 'Flags 1990\'s-style decade plurals.',
    passCriteria: 'Decade plurals without apostrophe (1990s).',
  },
  'ai-numbers-ranges-text': {
    howChecked: 'Flags en/em-dash numeric ranges with units in body text.',
    passCriteria: 'Running-text ranges use "to"/"through".',
  },
  'ai-punct-nested-parens': {
    howChecked: 'Detects parentheses nested inside parentheses.',
    passCriteria: 'Inner parentheticals use square brackets.',
  },
  'ai-punct-colon-because': {
    howChecked: 'Flags "because:", "include:", "due to:" before inline lists.',
    passCriteria: 'No colon after because/include unless a vertical list follows.',
  },
  'ai-ref-section-present': {
    howChecked: 'Finds References/Bibliography section and checks numeric entry markers.',
    passCriteria: 'References section exists with numerical ordering.',
  },
  'ai-ref-citation-style': {
    howChecked: 'Detects Roman/[n] citation patterns and missing <sup> markup.',
    passCriteria: 'Superscript Arabic numeral citations preferred.',
  },
  'ai-ref-personal-comms': {
    howChecked: 'Scans References section for personal communication / unpublished entries.',
    passCriteria: 'Personal communications excluded from numbered list.',
  },
  'ai-ref-external-links': {
    howChecked: 'Flags <a href="http(s):..."> links inside References HTML.',
    passCriteria: 'URLs shown as plain text, not external hyperlinks.',
  },
  'ai-ref-min-elements': {
    howChecked: 'Heuristically checks reference entries for year and source markers.',
    passCriteria: 'Each reference has author/title/source/date retrievability elements.',
  },
  'ai-ref-doi-format': {
    howChecked: 'Flags doi: prefix and trailing periods after DOI URLs.',
    passCriteria: 'DOI as https://doi.org/... without trailing period.',
  },
  'ai-table-caption-format': {
    howChecked: 'Validates Table/Figure numbering tokens for Roman/letters vs Section-N form.',
    passCriteria: 'Arabic section-based captions (e.g., Table 1-1).',
  },
  'ai-lot-lof': {
    howChecked: 'For long documents with tables/figures, requires List of Tables/Figures sections.',
    passCriteria: 'LOT/LOF present when applicable.',
  },
  'ai-footnote-markers': {
    howChecked: 'Flags *, †, ‡, § footnote markers.',
    passCriteria: 'Sequential superscript lowercase letters.',
  },
  'ai-appendix-numbering': {
    howChecked: 'Detects mixed letter/number appendix numbering schemes.',
    passCriteria: 'One sequential appendix numbering scheme.',
  },
  'ai-footer-confidential': {
    howChecked: 'Searches section text for Confidential label (footer expectation).',
    passCriteria: 'Confidential appears; export footers should use X of Y.',
  },
  'ai-pagination-x-of-y': {
    howChecked: 'Flags Page N patterns without "of Y".',
    passCriteria: 'Pagination uses X of Y format.',
  },
  'ai-paper-size-agency': {
    howChecked: 'Compares FDA/EMA mentions with A4 vs US Letter language.',
    passCriteria: 'Declared paper size matches target agency.',
  },
  'ai-toc-present': {
    howChecked: 'Requires Table of Contents section for long protocols.',
    passCriteria: 'TOC present for documents ≥ ~5 pages.',
  },
  'ai-blue-text': {
    howChecked: 'Detects blue/#0000ff inline CSS colors in section HTML.',
    passCriteria: 'No blue body/figure text (blue reserved for hyperlinks).',
  },
  'ai-cite-before-placement': {
    howChecked: 'Checks whether Table/Figure caption appears before any earlier citation.',
    passCriteria: 'Elements are cited in text before placement.',
  },
  'ai-kg-entity-inventory': {
    howChecked: 'Counts MENTIONS_ENTITY/DEFINES_ENTITY links from Protocol Content.',
    passCriteria: 'Entity extraction present for deep KG checks.',
  },
  'ai-kg-design-consistency': {
    howChecked: 'Compares design keywords and StudyDesignType entities.',
    passCriteria: 'Single consistent design type.',
  },
  'ai-kg-soa-procedure-overlap': {
    howChecked: 'Bidirectional procedure-term overlap between SoA and body sections.',
    passCriteria: 'SoA and body procedures match.',
  },
  'ai-kg-visit-windows': {
    howChecked: 'Looks for visit window language when visits are scheduled.',
    passCriteria: 'Visit windows defined.',
  },
  'ai-kg-rescue-estimand': {
    howChecked: 'Links rescue therapy mentions to estimand/ICE language.',
    passCriteria: 'Rescue rules consistent with estimands.',
  },
  'ai-kg-randomization-iwrs': {
    howChecked: 'Checks randomization, stratification, and IWRS/IRT documentation.',
    passCriteria: 'Randomization plan complete and analyzable.',
  },
  'ai-kg-blinding-unblind': {
    howChecked: 'If blinded, requires emergency unblinding documentation.',
    passCriteria: 'Blinding and unblinding documented.',
  },
  'ai-kg-analysis-sets': {
    howChecked: 'Text + AnalysisSet entities for ITT/PP/Safety populations.',
    passCriteria: 'Analysis populations precisely defined.',
  },
  'ai-kg-abbr-conflicts': {
    howChecked: 'Finds abbreviation properties with multiple expansions in graph.',
    passCriteria: 'One expansion per abbreviation.',
  },
  'ai-kg-lab-units': {
    howChecked: 'LaboratoryTest unit conflicts and mixed mg/dL vs mmol/L text.',
    passCriteria: 'Consistent lab units.',
  },
  'ai-kg-criteria-screening': {
    howChecked: 'Matches eligibility requirements to SoA screening assessments.',
    passCriteria: 'Criteria have matching screening assessments.',
  },
  'ai-kg-product-naming': {
    howChecked: 'Canonical drug from synced facts vs protocol text.',
    passCriteria: 'Consistent product naming.',
  },
  'ai-kg-endpoint-analysis-align': {
    howChecked: 'Co-occurrence checks for objectives/endpoints/estimands/ICE.',
    passCriteria: 'Endpoints and analyses align.',
  },
  'ai-kg-hru-tools': {
    howChecked: 'HRU endpoints require PRO/instrument language.',
    passCriteria: 'HRU tools defined.',
  },
  'ai-kg-contacts-roles': {
    howChecked: 'Contact/role conflict structural check.',
    passCriteria: 'No conflicting sponsor/CRO/site roles.',
  },
  'ai-kg-skipped': {
    howChecked: 'Attempts Neo4j connection and queries expected facts for the study ID.',
    passCriteria: 'Knowledge Graph available with synced study facts.',
  },
  'ai-kg-facts-consistency': {
    howChecked: 'Compares drug name, sample size, and phase from Neo4j to protocol narrative text.',
    passCriteria: 'Protocol text aligns with Knowledge Graph expected facts.',
  },
  'ai-gemini-synopsis-flow': {
    howChecked: 'Gemini 2.5 Pro reviews synopsis/objectives/endpoints/analysis logical flow.',
    passCriteria: 'Synopsis follows rationale → objectives → endpoints → analysis.',
  },
  'ai-gemini-objective-endpoint': {
    howChecked: 'Gemini evaluates whether objectives have measurable, aligned endpoints.',
    passCriteria: 'Objectives and endpoints are clearly linked and measurable.',
  },
  'ai-gemini-ip-uniform': {
    howChecked: 'Gemini checks investigational product naming consistency across sections.',
    passCriteria: 'IP/brand/generic names used uniformly.',
  },
  'ai-gemini-soa-text': {
    howChecked: 'Gemini compares Schedule of Activities / procedures text to assessment descriptions.',
    passCriteria: 'SoA and procedure text are mutually consistent.',
  },
  'ai-gemini-template-language': {
    howChecked: 'Gemini reviews template/boilerplate language for consistency.',
    passCriteria: 'Template language is consistent and correctly applied.',
  },
  'ai-gemini-tone-professional': {
    howChecked: 'Gemini scans for slang, informal tone, and non-objective clinical claims.',
    passCriteria: 'Professional, objective regulatory tone throughout.',
  },
  'ai-gemini-study-design-consistency': {
    howChecked: 'Gemini compares study design wording between synopsis and body.',
    passCriteria: 'Study design type is consistent throughout.',
  },
  'ai-gemini-dose-route-regimen': {
    howChecked: 'Gemini cross-checks dose, route, and regimen across synopsis and treatment sections.',
    passCriteria: 'Dose/route/regimen identical across sections.',
  },
  'ai-gemini-safety-reporting': {
    howChecked: 'Gemini verifies SAE/SUSAR reporting timelines are stated.',
    passCriteria: 'Explicit safety reporting timelines present.',
  },
  'ai-gemini-data-privacy-specimens': {
    howChecked: 'Gemini checks for data privacy/confidentiality and specimen handling content.',
    passCriteria: 'Privacy and specimen handling are addressed.',
  },
  'ai-gemini-table-figure-data': {
    howChecked: 'Gemini compares table/figure narrative claims to surrounding text where visible.',
    passCriteria: 'Table/figure content aligns with narrative.',
  },
  'ai-gemini-terminology-consistency': {
    howChecked: 'Gemini flags switching between Study/Trial, endpoint/end point, timepoint/time point, etc.',
    passCriteria: 'One preferred term per concept throughout.',
  },
  'ai-gemini-intake-align': {
    howChecked: 'Gemini compares clinical intake summary to generated protocol sections.',
    passCriteria: 'Protocol aligns with clinical intake source data.',
  },
  'ai-gemini-batch': {
    howChecked: 'Gemini semantic validation batch.',
    passCriteria: 'All semantic checks completed successfully.',
  },
  'ai-intake-quality': {
    howChecked:
      'Loads clinical intake and detects generic placeholder text in benefits_investigational and risks_investigational.',
    passCriteria: 'Benefits and risks fields contain study-specific content, not framework templates.',
  },
  'ai-intake-protocol-align': {
    howChecked:
      'Compares intake identifiers, drug names, endpoints, and sample size hints to protocol section text.',
    passCriteria: 'Key intake facts appear in the protocol narrative.',
  },
  'ai-intake-skipped': {
    howChecked: 'Attempts to load clinical intake record for the project.',
    passCriteria: 'Clinical intake exists for cross-check.',
  },
  'ai-intake-load': {
    howChecked: 'Database fetch of latest clinical intake by project ID.',
    passCriteria: 'Clinical intake loaded successfully.',
  },
  m11: {
    howChecked: 'Verifies all required ICH M11 section titles are present in the document structure.',
    passCriteria: 'All mandatory M11 sections are included.',
  },
  font: {
    howChecked: 'Inline HTML font-family and font-size vs Draft 1.0 §4.1.',
    passCriteria: 'Times New Roman with allowed sizes.',
  },
};

/** Turn API/Gemini error objects (e.g. { location, description }) into display strings. */
export const formatValidationErrorMessage = (err) => {
  if (err == null) return '';
  if (typeof err === 'string') return err.trim();
  if (typeof err === 'number' || typeof err === 'boolean') return String(err);
  if (typeof err === 'object') {
    const location =
      err.location ?? err.subsection ?? err.section ?? err.field ?? err.title ?? '';
    const description =
      err.description ??
      err.message ??
      err.suggestion ??
      err.error ??
      err.text ??
      '';
    const loc = String(location).trim();
    const desc = String(description).trim();
    if (loc && desc) return `${loc}: ${desc}`;
    if (desc) return desc;
    if (loc) return loc;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Validation issue';
    }
  }
  return String(err);
};

export const normalizeValidationErrors = (errors) =>
  (Array.isArray(errors) ? errors : [])
    .map(formatValidationErrorMessage)
    .filter((line) => line.length > 0);

export const enrichValidationResult = (result) => {
  const catalog = VALIDATION_CHECK_CATALOG[result.id] || {};
  const isSkipped = Boolean(result.skipped);
  const passed = Boolean(result.passed);
  const errors = normalizeValidationErrors(result.errors);
  const methodology = result.methodology || 'rule';

  let status = 'failed';
  if (isSkipped) status = 'skipped';
  else if (passed) status = 'passed';

  let outcome = 'Failed';
  if (isSkipped) outcome = 'Skipped';
  else if (passed) outcome = errors.length > 0 ? 'Passed with notes' : 'Passed';

  const howChecked =
    result.howChecked ||
    catalog.howChecked ||
    (methodology === 'ai'
      ? 'Automated review using Google Gemini 2.5 Pro against Dizzaroo style guide rules.'
      : methodology === 'kg'
        ? 'Automated comparison against Neo4j Knowledge Graph facts.'
        : 'Automated rule-based scan of protocol section HTML and plain text.');

  const passCriteria = result.passCriteria || catalog.passCriteria || 'Meets Dizzaroo / ICH validation criteria.';

  const successDetail =
    passed && !isSkipped
      ? result.successDetail ||
        (errors.length === 0
          ? `Check completed successfully. ${passCriteria}`
          : passCriteria)
      : '';

  const failureDetail =
    !passed && !isSkipped
      ? errors.length > 0
        ? errors
        : ['Check did not pass; no specific findings were recorded.']
      : isSkipped
        ? errors
        : [];

  return {
    ...result,
    status,
    outcome,
    howChecked,
    passCriteria,
    successDetail,
    failureDetail,
    methodologyLabel:
      methodology === 'ai'
        ? 'AI (Gemini)'
        : methodology === 'kg'
          ? 'Knowledge Graph'
          : 'Rule-based',
  };
};

export const enrichValidationResults = (results = []) =>
  (Array.isArray(results) ? results : []).map(enrichValidationResult);
